import React from "react";
import styled from "styled-components";
import { device } from "../../constants/mediaQueries";
import { getAppType, isAppTypeIframe } from "../../getAppType";
import { useSettings } from "../../settings/useSettings";
import { useConsentsForCandidate } from "../../user/useConsentsForCandidate";
import { concatClassnames } from "../../utils/style/concatClassnames";
import { useMediaQuery } from "../../utils/style/useMediaQuery";
import { MissingConsentMessage } from "../messages/MissingConsentMessage";
import { useLayoutPadding } from "./useLayoutpadding";

interface PageLayoutProps {
  children: any;
  extraPadding?: boolean;
  style?: React.CSSProperties;
  isAdminPage?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
}

interface Message {
  /** Messages must have a predetermined height because of complications related to our inner scrollbar and preventing ui jumping around */
  height: number;
  render: () => JSX.Element;
  show?: boolean;
  key: string;
}

export const PageLayout = (props: PageLayoutProps) => {
  const { children, extraPadding, isAdminPage, style, containerRef } = props;

  const settings = useSettings();
  const hasTopBar = !isAdminPage && !!settings.showTopBar;

  // Get missing consents for the user, and check if the user is missing the 5000 consent
  const { notActiveConsents } = useConsentsForCandidate();

  // Display a message if the user has not active consents
  const notActiveConsentsMessages: Message[] = [
    {
      height: 3,
      render: () => (
        <MissingConsentMessage
          style={{ padding: "0 0.75rem", height: "3rem" }}
        />
      ),
      show: notActiveConsents.length > 0,
      key: "missingConsent",
    },
  ];

  // Determine the height of the messages container and the messages to render
  const messagesToRender = notActiveConsentsMessages.filter(
    (message) => message.show
  );
  const messagesHeight = messagesToRender.reduce(
    (acc, message) => acc + message.height,
    0
  );

  // margin-top + margin-bot + gaps + sum of heights
  let messageContainerHeight =
    messagesToRender.length > 0
      ? 1 + messagesToRender.length + messagesHeight
      : 0;

  // Determine if there are messages to render
  const hasMessageTop = messagesToRender.length > 0;

  // Get the padding for the layout
  const zeroPadding =
    isAdminPage || settings.idEmbed === "cvpScriptNextJobPreview";
  const { padTop, padBot, padLeft, padRight } = useLayoutPadding(
    extraPadding,
    zeroPadding,
    hasTopBar,
    hasMessageTop
  );

  // Media query for alle screen sizes above md
  const aboveMd = useMediaQuery(device.md);

  const topBarHeight = hasTopBar ? "3rem" : "0rem";

  // Available content height for the main content after all of the other elements have been accounted for
  // This is the height that the main content should take up when screen size is above md

  const _availableContentHeight = `calc(100 * var(--vh) - ${topBarHeight} - ${messageContainerHeight}rem - ${padTop} - ${padBot})`;
  const availableContentHeight = aboveMd
    ? isAppTypeIframe()
      ? `min(max(${_availableContentHeight}, 600px), 1000px)`
      : _availableContentHeight
    : "auto";

  return (
    <StyledPageLayout
      className={concatClassnames([
        "cvp-page-layout",
        `cvp-page-layout-${getAppType()}`,
        isAdminPage && `cvp-page-layout-admin`,
      ])}
      ref={containerRef}
      style={
        {
          ...style,
          "--available-content-height": availableContentHeight,
          paddingTop: padTop,
          paddingBottom: padBot,
          paddingLeft: padLeft,
          paddingRight: padRight,
        } as any
      }
    >
      <div
        className="cvp-layout-messages-container"
        style={{
          padding: messagesToRender.length ? "1rem 0" : 0,
        }}
      >
        {messagesToRender.map((m) => (
          <React.Fragment key={m.key}>{m.render()}</React.Fragment>
        ))}
      </div>

      <div>{children}</div>
    </StyledPageLayout>
  );
};

const StyledPageLayout = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: min(100vw, 2000px);
  flex-grow: 1;

  &.cvp-page-layout-admin {
    max-width: 100vw;
  }
`;
